import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../../config/api.js';
import AdvocateFiltersSection from './performance/AdvocateFiltersSection.js';
import DailyLawyerDashboardSection from './performance/DailyLawyerDashboardSection.js';
import DistrictDrillDownServiceReferralSection from './performance/DistrictDrillDownServiceReferralSection.js';

const DEFAULT_ROLE_RID = 5;
const TOP_N_OPTIONS = [5, 10, 15, 20, 25, 50];

const todayIso = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());

const formatCount = (value, pad2 = false) => {
  const safe = Math.max(0, Number(value) || 0);
  const text = String(safe);
  return pad2 ? text.padStart(2, '0') : text;
};

const buildRangeLabel = (dateFrom, dateTo) => {
  const today = todayIso();
  if (dateFrom === today && dateTo === today) return 'Today';
  if (dateFrom === dateTo) return dateFrom;
  return `${dateFrom} to ${dateTo}`;
};

const validateRange = (dateFrom, dateTo) => {
  const today = todayIso();

  if (!isIsoDate(dateFrom)) return 'Invalid "Date From".';
  if (!isIsoDate(dateTo)) return 'Invalid "Date To".';
  if (dateFrom > today) return "Date From can't be greater than today.";
  if (dateTo > today) return "Date To can't be greater than today.";
  if (dateFrom > dateTo) return 'Date From cannot be after Date To.';

  return '';
};

const parseJsonResponse = async (response) => {
  const text = await response.text();
  let json = null;

  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    throw new Error(json?.message || json?.error || text || 'Request failed');
  }

  if (json?.success === false) {
    throw new Error(json?.message || json?.error || 'Request failed');
  }

  return json;
};

const extractRows = (json) => {
  const list =
    Array.isArray(json?.data?.data) ? json.data.data :
    Array.isArray(json?.data) ? json.data :
    Array.isArray(json?.result) ? json.result :
    Array.isArray(json) ? json : [];

  return list
    .map((row, index) => {
      const name = String(
        row?.fullName ??
        row?.fullname ??
        row?.FullName ??
        row?.name ??
        row?.Name ??
        ''
      ).trim();

      if (!name) return null;

      return {
        id: `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-') || `lawyer-${index}`}-${index}`,
        name,
        total: Math.max(0, Number(row?.total ?? row?.Total ?? row?.createdby ?? row?.createdBy ?? row?.count ?? row?.Count ?? 0) || 0),
      };
    })
    .filter(Boolean);
};

const toSafeNumber = (value) => Math.max(0, Number(value) || 0);

const clampPercent = (value) => Math.max(0, Math.min(100, Number(value) || 0));

const formatPercentSigned = (value) => {
  const safe = Number(value) || 0;
  const rounded = Number(safe.toFixed(1));
  const prefix = rounded > 0 ? '+' : '';
  return `${prefix}${rounded}%`;
};

const formatCallSeconds = (value) => {
  const totalSeconds = Math.max(0, Math.round(Number(value) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

const extractTargetMetrics = (json) => {
  const root = json?.data ?? json ?? {};

  const monthlyTarget = toSafeNumber(root.monthlytarget ?? root.monthlyTarget ?? root.month_target ?? 0);
  const yearlyTarget = toSafeNumber(root.yearlytarget ?? root.yearlyTarget ?? root.year_target ?? 0);
  const monthlyClosed = toSafeNumber(root.monthlyClosed ?? root.monthlyclosed ?? root.monthlyActual ?? root.monthlyactual ?? root.thisMonthClosed ?? 0);
  const yearlyClosed = toSafeNumber(root.yearlyClosed ?? root.yearlyclosed ?? root.yearlyActual ?? root.yearlyactual ?? root.thisYearClosed ?? 0);

  const monthlyProgress = monthlyTarget > 0 ? clampPercent((monthlyClosed / monthlyTarget) * 100) : 0;
  const yearlyProgress = yearlyTarget > 0 ? clampPercent((yearlyClosed / yearlyTarget) * 100) : 0;

  const monthlyTrendPercentRaw = Number(
    root.monthlyTrendPercent ??
    root.monthlytrendpercent ??
    root.monthlyDeltaPercent ??
    root.monthlydeltapercent ??
    root.monthlyVsLastMonthPercent ??
    0
  ) || 0;

  const yearlyTrendPercentRaw = Number(
    root.yearlyTrendPercent ??
    root.yearlytrendpercent ??
    root.yearlyDeltaPercent ??
    root.yearlydeltapercent ??
    root.yearlyVsPlanPercent ??
    (yearlyTarget > 0 ? ((yearlyClosed - yearlyTarget) / yearlyTarget) * 100 : 0)
  ) || 0;

  return {
    monthlyTarget,
    yearlyTarget,
    monthlyClosed,
    yearlyClosed,
    monthlyProgress,
    yearlyProgress,
    monthlyTrendPercent: monthlyTrendPercentRaw,
    yearlyTrendPercent: yearlyTrendPercentRaw,
  };
};

const DEFAULT_TARGET_METRICS = {
  monthlyTarget: 0,
  yearlyTarget: 0,
  monthlyClosed: 0,
  yearlyClosed: 0,
  monthlyProgress: 0,
  yearlyProgress: 0,
  monthlyTrendPercent: 0,
  yearlyTrendPercent: 0,
};

const formatDurationFromDate = (dateValue) => {
  if (!dateValue) return '0d 0h 0m';
  const created = new Date(dateValue);
  if (Number.isNaN(created.getTime())) return '0d 0h 0m';

  const diffMs = Math.max(0, Date.now() - created.getTime());
  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;
  return `${days}d ${hours}h ${minutes}m`;
};

const extractOldestPending = (json) => {
  const root = json?.data ?? json ?? {};
  const oldestCreatedOn =
    root.oldestPendingCreatedOn ??
    root.oldestpendingcreatedon ??
    root.createdon ??
    root.CreatedOn ??
    null;

  const queryId =
    root.complaintid ??
    root.complaintId ??
    root.ComplaintID ??
    root.queryId ??
    root.queryid ??
    root.QID ??
    root.qid ??
    root.id ??
    '';

  const ageLabel =
    root.ageLabel ??
    root.agelabel ??
    formatDurationFromDate(oldestCreatedOn);

  return {
    ageLabel,
    queryId: String(queryId || '').trim(),
  };
};

const STATUS_KEYS = ['initiated', 'completed', 'approved', 'refer', 'corrected', 'advised', 'closed'];

const STATUS_LABELS = {
  initiated: 'Initiated',
  completed: 'Completed',
  approved: 'Approved',
  refer: 'Refer',
  corrected: 'Corrected',
  advised: 'Advised',
  closed: 'Closed',
};

const extractCaseStatusMix = (json) => {
  const root = json?.data ?? json ?? {};

  const total = Math.max(1, Number(root.total ?? root.Total ?? 0) || 1);

  const statuses = STATUS_KEYS.map((key) => {
    const count = Math.max(0, Number(
      root[key] ?? root[key.charAt(0).toUpperCase() + key.slice(1)] ?? 0
    ) || 0);
    const percent = total > 0 ? Number(((count / total) * 100).toFixed(1)) : 0;
    return { key, label: STATUS_LABELS[key], count, percent };
  });

  return { total: Number(root.total ?? root.Total ?? 0) || 0, statuses };
};

const DEFAULT_CASE_STATUS_MIX = {
  total: 0,
  statuses: STATUS_KEYS.map((key) => ({ key, label: STATUS_LABELS[key], count: 0, percent: 0 })),
};

const extractLast7Days = (json) => {
  const root = json?.data ?? json ?? {};

  const total = Math.max(0, Number(root.total ?? root.Total ?? root.totalCount ?? root.totalcount ?? 0) || 0);
  const avgPerDay = Number(root.avgPerDay ?? root.avgperday ?? root.avg_per_day ?? 0) || (total > 0 ? Math.round(total / 7) : 0);

  const previousTotal = Math.max(0, Number(root.previousTotal ?? root.previoustotal ?? root.previous_total ?? 0) || 0);
  const changePercent = Number(
    root.changePercent ??
    root.changepercent ??
    root.percentChange ??
    (previousTotal > 0 ? ((total - previousTotal) / previousTotal) * 100 : 0)
  ) || 0;

  return { total, avgPerDay, changePercent };
};

const extractTodayVsYesterday = (json) => {
  const root = json?.data ?? json ?? {};

  const todayCount = Math.max(0, Number(root.today ?? root.todayCount ?? root.todaycount ?? 0) || 0);
  const yesterdayCount = Math.max(0, Number(root.yesterday ?? root.yesterdayCount ?? root.yesterdaycount ?? 0) || 0);

  const changePercent = Number(
    root.changePercent ??
    root.changepercent ??
    root.percentChange ??
    (yesterdayCount > 0 ? ((todayCount - yesterdayCount) / yesterdayCount) * 100 : 0)
  ) || 0;

  return {
    todayCount,
    yesterdayCount,
    changePercent,
  };
};

const extractAverageCallTime = (json) => {
  const root = json?.data ?? json ?? {};
  const avgCallSeconds = Math.max(0, Number(root.avgCallSeconds ?? root.avg_call_seconds ?? root.averageSeconds ?? 0) || 0);
  const targetSeconds = Math.max(0, Number(root.targetSeconds ?? root.target_seconds ?? 1200) || 1200);
  const differenceSeconds = Number(root.differenceSeconds ?? root.difference_seconds ?? (avgCallSeconds - targetSeconds)) || 0;
  return { avgCallSeconds, targetSeconds, differenceSeconds };
};

const BackofficePerformancePanel = ({ isActive }) => {
  const today = useMemo(() => todayIso(), []);
  const hasAutoLoadedRef = useRef(false);

  const [dashboardForm, setDashboardForm] = useState({
    dateFrom: today,
    dateTo: today,
    topN: '10',
  });
  const [dashboardRows, setDashboardRows] = useState([]);
  const [dashboardTotal, setDashboardTotal] = useState(0);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardErr, setDashboardErr] = useState('');
  const [dashboardAppliedRange, setDashboardAppliedRange] = useState({ from: today, to: today, topN: 10 });

  const [filterForm, setFilterForm] = useState({
    dateFrom: today,
    dateTo: today,
    topN: '10',
    lawyerId: 'all',
  });
  const [filterRows, setFilterRows] = useState([]);
  const [filterTotal, setFilterTotal] = useState(0);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterErr, setFilterErr] = useState('');
  const [filterAppliedRange, setFilterAppliedRange] = useState({ from: today, to: today, topN: 10 });
  const [targetMetrics, setTargetMetrics] = useState(DEFAULT_TARGET_METRICS);
  const [targetMetricsLoading, setTargetMetricsLoading] = useState(false);
  const [targetMetricsErr, setTargetMetricsErr] = useState('');
  const [oldestPending, setOldestPending] = useState({ ageLabel: '0d 0h 0m', queryId: '' });
  const [oldestPendingErr, setOldestPendingErr] = useState('');
  const [averageCallTime, setAverageCallTime] = useState({ avgCallSeconds: 0, targetSeconds: 1200, differenceSeconds: -1200 });
  const [averageCallTimeLoading, setAverageCallTimeLoading] = useState(false);
  const [todayVsYesterday, setTodayVsYesterday] = useState({ todayCount: 0, yesterdayCount: 0, changePercent: 0 });
  const [last7Days, setLast7Days] = useState({ total: 0, avgPerDay: 0, changePercent: 0 });
  const [last7DaysLoading, setLast7DaysLoading] = useState(false);
  const [caseStatusMix, setCaseStatusMix] = useState(DEFAULT_CASE_STATUS_MIX);
  const [caseStatusMixLoading, setCaseStatusMixLoading] = useState(false);
  const [callerType, setCallerType] = useState({ fresh: 0, repeater: 0, repeaterPercent: 0 });
  const [callerTypeLoading, setCallerTypeLoading] = useState(false);
  const [feedback, setFeedback] = useState({ satisfy: 0, unsatisfy: 0, neutral: 0, total: 0, satisfyPct: 0, unsatisfyPct: 0, neutralPct: 0 });
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const fetchPerformance = useCallback(async ({ dateFrom, dateTo, topN }) => {
    const params = new URLSearchParams({
      dateFrom,
      dateTo,
      roleRid: String(DEFAULT_ROLE_RID),
      topN: String(topN),
    });

    const response = await fetch(`${API_BASE}/advocate-performance?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });

    const json = await parseJsonResponse(response);
    const rows = extractRows(json);
    const total = rows.reduce((sum, row) => sum + (Number(row.total) || 0), 0);

    return { rows, total };
  }, []);

  const loadDashboard = useCallback(async ({ dateFrom, dateTo, topN }) => {
    const rangeErr = validateRange(dateFrom, dateTo);
    if (rangeErr) {
      setDashboardErr(rangeErr);
      return;
    }

    try {
      setDashboardLoading(true);
      setDashboardErr('');
      const { rows, total } = await fetchPerformance({ dateFrom, dateTo, topN });
      setDashboardRows(rows);
      setDashboardTotal(total);
      setDashboardAppliedRange({ from: dateFrom, to: dateTo, topN });
    } catch (error) {
      setDashboardRows([]);
      setDashboardTotal(0);
      setDashboardErr(error?.message || 'Failed to load daily lawyer dashboard');
    } finally {
      setDashboardLoading(false);
    }
  }, [fetchPerformance]);

  const loadFilterRows = useCallback(async ({ dateFrom, dateTo, topN, preserveLawyerId = null }) => {
    const rangeErr = validateRange(dateFrom, dateTo);
    if (rangeErr) {
      setFilterErr(rangeErr);
      return;
    }

    try {
      setFilterLoading(true);
      setFilterErr('');
      const { rows, total } = await fetchPerformance({ dateFrom, dateTo, topN });
      setFilterRows(rows);
      setFilterTotal(total);
      setFilterAppliedRange({ from: dateFrom, to: dateTo, topN });

      const keepLawyerId = preserveLawyerId ?? filterForm.lawyerId;
      const selectedExists = keepLawyerId !== 'all' && rows.some((row) => row.id === keepLawyerId);
      setFilterForm((prev) => ({
        ...prev,
        lawyerId: selectedExists ? keepLawyerId : 'all',
      }));
    } catch (error) {
      setFilterRows([]);
      setFilterTotal(0);
      setFilterErr(error?.message || 'Failed to load advocate query counts');
    } finally {
      setFilterLoading(false);
    }
  }, [fetchPerformance, filterForm.lawyerId]);

  const loadTargetMetrics = useCallback(async () => {
    try {
      setTargetMetricsLoading(true);
      setTargetMetricsErr('');

      const response = await fetch(`${API_BASE}/performance/targets-summary`, {
        headers: { Accept: 'application/json' },
      });

      const json = await parseJsonResponse(response);
      const metrics = extractTargetMetrics(json);
      setTargetMetrics(metrics);
    } catch (error) {
      setTargetMetrics(DEFAULT_TARGET_METRICS);
      setTargetMetricsErr(error?.message || 'Failed to load performance targets');
    } finally {
      setTargetMetricsLoading(false);
    }
  }, []);

  const loadOldestPending = useCallback(async () => {
    try {
      setOldestPendingErr('');
      const response = await fetch(`${API_BASE}/performance/oldest-pending-query`, {
        headers: { Accept: 'application/json' },
      });
      const json = await parseJsonResponse(response);
      setOldestPending(extractOldestPending(json));
    } catch (error) {
      setOldestPending({ ageLabel: '0d 0h 0m', queryId: '' });
      setOldestPendingErr(error?.message || 'Failed to load oldest pending query');
    }
  }, []);

  const loadAverageCallTime = useCallback(async () => {
    try {
      setAverageCallTimeLoading(true);
      const params = new URLSearchParams({
        dateFrom: today,
        dateTo: today,
      });
      const response = await fetch(`${API_BASE}/performance/average-call-time?${params.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const json = await parseJsonResponse(response);
      setAverageCallTime(extractAverageCallTime(json));
    } catch {
      setAverageCallTime({ avgCallSeconds: 0, targetSeconds: 1200, differenceSeconds: -1200 });
    } finally {
      setAverageCallTimeLoading(false);
    }
  }, [today]);

  const loadCaseStatusMix = useCallback(async () => {
    try {
      setCaseStatusMixLoading(true);
      const response = await fetch(`${API_BASE}/performance/case-status-mix`, {
        headers: { Accept: 'application/json' },
      });
      const json = await parseJsonResponse(response);
      setCaseStatusMix(extractCaseStatusMix(json));
    } catch {
      setCaseStatusMix(DEFAULT_CASE_STATUS_MIX);
    } finally {
      setCaseStatusMixLoading(false);
    }
  }, []);

  const loadLast7Days = useCallback(async () => {
    try {
      setLast7DaysLoading(true);
      const response = await fetch(`${API_BASE}/performance/last-7-days`, {
        headers: { Accept: 'application/json' },
      });
      const json = await parseJsonResponse(response);
      setLast7Days(extractLast7Days(json));
    } catch {
      setLast7Days({ total: 0, avgPerDay: 0, changePercent: 0 });
    } finally {
      setLast7DaysLoading(false);
    }
  }, []);

  const loadTodayVsYesterday = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/performance/today-vs-yesterday`, {
        headers: { Accept: 'application/json' },
      });
      const json = await parseJsonResponse(response);
      setTodayVsYesterday(extractTodayVsYesterday(json));
    } catch {
      setTodayVsYesterday({ todayCount: 0, yesterdayCount: 0, changePercent: 0 });
    }
  }, []);

  const loadFeedback = useCallback(async () => {
    try {
      setFeedbackLoading(true);
      const params = new URLSearchParams({
        dateFrom: today,
        dateTo: today,
      });
      const response = await fetch(`${API_BASE}/feedback-stats?${params.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const json = await parseJsonResponse(response);
      const root = json?.data ?? json ?? {};
      const satisfy = Math.max(0, Number(root.satisfy ?? root.Satisfy ?? 0) || 0);
      const unsatisfy = Math.max(0, Number(root.unsatisfy ?? root.Unsatisfy ?? 0) || 0);
      const neutral = Math.max(0, Number(root.neutral ?? root.Neutral ?? 0) || 0);
      const total = satisfy + unsatisfy + neutral;
      setFeedback({
        satisfy,
        unsatisfy,
        neutral,
        total,
        satisfyPct: total > 0 ? Number(((satisfy / total) * 100).toFixed(1)) : 0,
        unsatisfyPct: total > 0 ? Number(((unsatisfy / total) * 100).toFixed(1)) : 0,
        neutralPct: total > 0 ? Number(((neutral / total) * 100).toFixed(1)) : 0,
      });
    } catch {
      setFeedback({ satisfy: 0, unsatisfy: 0, neutral: 0, total: 0, satisfyPct: 0, unsatisfyPct: 0, neutralPct: 0 });
    } finally {
      setFeedbackLoading(false);
    }
  }, [today]);

  const loadCallerType = useCallback(async () => {
    try {
      setCallerTypeLoading(true);
      const response = await fetch(`${API_BASE}/today-new-vs-repeated-callers`, {
        headers: { Accept: 'application/json' },
      });
      const json = await parseJsonResponse(response);
      const root = json?.data ?? json ?? {};
      const fresh = Math.max(0, Number(root.fresh_callers ?? root.NewCallers ?? root.newCallers ?? root.new_callers ?? 0) || 0);
      const repeater = Math.max(0, Number(root.repeated_callersq ?? root.repeated_callers ?? root.RepeatedCallers ?? root.repeatedCallers ?? 0) || 0);
      const total = fresh + repeater;
      const repeaterPercent = total > 0 ? Number(((repeater / total) * 100).toFixed(1)) : 0;
      setCallerType({ fresh, repeater, repeaterPercent });
    } catch {
      setCallerType({ fresh: 0, repeater: 0, repeaterPercent: 0 });
    } finally {
      setCallerTypeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isActive) {
      hasAutoLoadedRef.current = false;
      return;
    }
    if (hasAutoLoadedRef.current) return;

    hasAutoLoadedRef.current = true;

    loadDashboard({
      dateFrom: dashboardForm.dateFrom,
      dateTo: dashboardForm.dateTo,
      topN: Number(dashboardForm.topN) || 10,
    });

    loadFilterRows({
      dateFrom: filterForm.dateFrom,
      dateTo: filterForm.dateTo,
      topN: Number(filterForm.topN) || 10,
      preserveLawyerId: 'all',
    });

    loadTargetMetrics();
    loadOldestPending();
    loadAverageCallTime();
    loadTodayVsYesterday();
    loadLast7Days();
    loadCallerType();
    loadFeedback();
  }, [dashboardForm.dateFrom, dashboardForm.dateTo, dashboardForm.topN, filterForm.dateFrom, filterForm.dateTo, filterForm.topN, isActive, loadAverageCallTime, loadCallerType, loadDashboard, loadFeedback, loadFilterRows, loadLast7Days, loadOldestPending, loadTargetMetrics, loadTodayVsYesterday]);

  useEffect(() => {
    if (!isActive) return;

    loadCaseStatusMix();
    const interval = setInterval(loadCaseStatusMix, 30000);
    return () => clearInterval(interval);
  }, [isActive, loadCaseStatusMix]);

  const selectedLawyer = useMemo(() => {
    if (filterForm.lawyerId === 'all') return null;
    return filterRows.find((row) => row.id === filterForm.lawyerId) || null;
  }, [filterForm.lawyerId, filterRows]);

  if (!isActive) return null;

  const handleDashboardShow = () => {
    loadDashboard({
      dateFrom: dashboardForm.dateFrom,
      dateTo: dashboardForm.dateTo,
      topN: Number(dashboardForm.topN) || 10,
    });
  };

  const handleFilterApply = () => {
    loadFilterRows({
      dateFrom: filterForm.dateFrom,
      dateTo: filterForm.dateTo,
      topN: Number(filterForm.topN) || 10,
      preserveLawyerId: filterForm.lawyerId,
    });
  };

  return (
    <section className="module-panel active" id="module-performance">
      <section className="panel performance-dashboard reveal is-visible">
        <div className="panel-head">
          <div className="perf-title">
            <h3>Advocate Performance</h3>
            <span className="perf-pill">Daily lawyer dashboard</span>
          </div>
        </div>

        <DailyLawyerDashboardSection
          appliedRange={dashboardAppliedRange}
          buildRangeLabel={buildRangeLabel}
          error={dashboardErr}
          form={dashboardForm}
          formatCount={formatCount}
          loading={dashboardLoading}
          onShow={handleDashboardShow}
          rows={dashboardRows}
          setForm={setDashboardForm}
          today={today}
          topOptions={TOP_N_OPTIONS}
          total={dashboardTotal}
        />

        <AdvocateFiltersSection
          appliedRange={filterAppliedRange}
          buildRangeLabel={buildRangeLabel}
          error={filterErr}
          form={filterForm}
          formatCount={formatCount}
          loading={filterLoading}
          onApply={handleFilterApply}
          rows={filterRows}
          selectedLawyer={selectedLawyer}
          setForm={setFilterForm}
          today={today}
          topOptions={TOP_N_OPTIONS}
          total={filterTotal}
        />

        <div className="perf-section">
          <div className="perf-section-head">
            <h4>Performance Metrics</h4>
            <span className="perf-pill">Counts + %</span>
          </div>
          <div className="perf-grid">
            <article className="perf-card kpi">
              <div className="perf-card-head">
                <span>Monthly Target</span>
                <span className={`trend ${targetMetrics.monthlyTrendPercent >= 0 ? 'up' : 'down'}`}>
                  {`${formatPercentSigned(targetMetrics.monthlyTrendPercent)} vs last month`}
                </span>
              </div>
              <div className="perf-metric">
                {targetMetrics.monthlyClosed.toLocaleString()}
                <small>{` / ${targetMetrics.monthlyTarget.toLocaleString()}`}</small>
              </div>
              <div className="perf-progress"><span style={{ width: `${targetMetrics.monthlyProgress}%` }}></span></div>
            </article>

            <article className="perf-card kpi">
              <div className="perf-card-head">
                <span>Annual Target</span>
                <span className={`trend ${targetMetrics.yearlyTrendPercent >= 0 ? 'up' : 'down'}`}>
                  {`${formatPercentSigned(targetMetrics.yearlyTrendPercent)} vs plan`}
                </span>
              </div>
              <div className="perf-metric">
                {targetMetrics.yearlyClosed.toLocaleString()}
                <small>{` / ${targetMetrics.yearlyTarget.toLocaleString()}`}</small>
              </div>
              <div className="perf-progress"><span style={{ width: `${targetMetrics.yearlyProgress}%` }}></span></div>
            </article>

            {targetMetricsLoading ? <div className="perf-sub">Loading target metrics...</div> : null}
            {targetMetricsErr ? <div className="perf-empty">{targetMetricsErr}</div> : null}

            <article className="perf-card">
              <div className="perf-card-head">
                <span>Average Call Time</span>
                <span className="perf-pill">Today</span>
              </div>
              <div className="perf-metric">
                {averageCallTimeLoading ? 'Loading...' : formatCallSeconds(averageCallTime.avgCallSeconds)}
              </div>
            </article>

            <article className="perf-card">
              <div className="perf-card-head">
                <span>Oldest Pending Query</span>
                <span className="perf-pill">Live</span>
              </div>
              <div className="perf-metric">{oldestPending.ageLabel}</div>
              <div className="perf-sub">{oldestPending.queryId ? `Queue ID: ${oldestPending.queryId}` : 'Queue ID: N/A'}</div>
              {oldestPendingErr ? <div className="perf-sub">{oldestPendingErr}</div> : null}
            </article>

            <article className="perf-card wide">
              <div className="perf-card-head">
                <span>Daily Case Status Mix</span>
                <span className="perf-pill">Total: {caseStatusMix.total.toLocaleString()}</span>
              </div>
              {caseStatusMixLoading ? (
                <div className="perf-sub">Loading...</div>
              ) : (
                <div className="perf-split">
                  {caseStatusMix.statuses.map((s) => (
                    <div key={s.key}>
                      <strong>{s.label}</strong>
                      <div className="perf-metric">{s.count.toLocaleString()} <small>({s.percent}%)</small></div>
                      <div className="perf-progress"><span style={{ width: `${s.percent}%` }}></span></div>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <article className="perf-card">
              <div className="perf-card-head">
                <span>Today vs Yesterday</span>
                <span className={`trend ${todayVsYesterday.changePercent >= 0 ? 'up' : 'down'}`}>
                  {formatPercentSigned(todayVsYesterday.changePercent)}
                </span>
              </div>
              <div className="perf-mini-grid">
                <div><span>Today</span><strong>{todayVsYesterday.todayCount.toLocaleString()}</strong></div>
                <div><span>Yesterday</span><strong>{todayVsYesterday.yesterdayCount.toLocaleString()}</strong></div>
              </div>
            </article>

            <article className="perf-card">
              <div className="perf-card-head">
                <span>Last 7 Days</span>
                <span className={`trend ${last7Days.changePercent >= 0 ? 'up' : 'down'}`}>
                  {formatPercentSigned(last7Days.changePercent)}
                </span>
              </div>
              {last7DaysLoading ? (
                <div className="perf-sub">Loading...</div>
              ) : (
                <div className="perf-mini-grid">
                  <div><span>Avg/Day</span><strong>{last7Days.avgPerDay.toLocaleString()}</strong></div>
                  <div><span>Total</span><strong>{last7Days.total.toLocaleString()}</strong></div>
                </div>
              )}
            </article>

            <article className="perf-card">
              <div className="perf-card-head">
                <span>After 5:00 pm Calls</span>
                <span className="perf-pill">Today</span>
              </div>
              <div className="perf-metric">54</div>
              <div className="perf-sub">Next-day follow-ups: 21</div>
            </article>

            <article className="perf-card">
              <div className="perf-card-head">
                <span>Workforce Strength</span>
                <span className="perf-pill">Lawyers/Agents</span>
              </div>
              <div className="perf-mini-grid">
                <div><span>Total</span><strong>64</strong></div>
                <div><span>Present</span><strong>51</strong></div>
                <div><span>WFH</span><strong>7</strong></div>
                <div><span>Absent</span><strong>6</strong></div>
              </div>
            </article>

            <article className="perf-card">
              <div className="perf-card-head">
                <span>Caller Type</span>
                <span className="perf-pill">{`${callerType.repeaterPercent}% repeaters`}</span>
              </div>
              {callerTypeLoading ? (
                <div className="perf-sub">Loading...</div>
              ) : (
                <div className="perf-mini-grid">
                  <div><span>Fresh</span><strong>{callerType.fresh.toLocaleString()}</strong></div>
                  <div><span>Repeater</span><strong>{callerType.repeater.toLocaleString()}</strong></div>
                </div>
              )}
            </article>

            <article className="perf-card">
              <div className="perf-card-head">
                <span>Caller Satisfaction</span>
                <span className="perf-pill">Total: {feedback.total}</span>
              </div>
              {feedbackLoading ? (
                <div className="perf-sub">Loading...</div>
              ) : (
                <div className="perf-sub">
                  Satisfy: {feedback.satisfy} ({feedback.satisfyPct}%) · Neutral: {feedback.neutral} ({feedback.neutralPct}%) · Unsatisfy: {feedback.unsatisfy} ({feedback.unsatisfyPct}%)
                </div>
              )}
            </article>

            <DistrictDrillDownServiceReferralSection />
          </div>
        </div>
      </section>
    </section>
  );
};

export default BackofficePerformancePanel;
